/**
 * Testable Message Input Component
 * Wrapper around @chatscope MessageInput that exposes a standard textarea for testing
 */

import { useState, useRef, KeyboardEvent } from 'react';

interface TestableMessageInputProps {
  placeholder?: string;
  onSend: (message: string) => void;
  disabled?: boolean;
  attachButton?: boolean;
  onAttachClick?: () => void;
  'data-testid'?: string;
}

export function TestableMessageInput({
  placeholder = 'Type a message...',
  onSend,
  disabled = false,
  attachButton = false,
  onAttachClick,
  'data-testid': testId = 'message-input',
}: TestableMessageInputProps) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = () => {
    if (value.trim() && !disabled) {
      onSend(value.trim());
      setValue('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const target = e.currentTarget;
    setValue(target.value);

    // Auto-resize textarea
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
  };

  return (
    <div
      className="flex items-end gap-2 p-3 border-t border-border bg-card"
      data-testid={testId}
    >
      {attachButton && (
        <button
          type="button"
          onClick={onAttachClick}
          disabled={disabled}
          className="p-2 border-0 bg-transparent cursor-pointer text-xl disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
          aria-label="Attach file"
        >
          📎
        </button>
      )}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        className="flex-1 px-3 py-2 text-base font-[inherit] border border-input rounded resize-none min-h-[44px] max-h-[120px] disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-ring"
      />
      <button
        type="button"
        onClick={handleSend}
        disabled={disabled || !value.trim()}
        className="px-4 py-2 text-sm font-semibold text-primary-foreground border-0 rounded cursor-pointer min-h-[44px] shrink-0 disabled:cursor-not-allowed bg-primary disabled:bg-muted-foreground/30"
        data-testid="send-button"
      >
        Send
      </button>
    </div>
  );
}
